import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../config/apiConfig';

// --- Axios Interceptors ---

axios.interceptors.request.use(
    (config) => {
        // Try to get token from multiple possible locations
        let token = sessionStorage.getItem('authToken');

        if (!token) {
            try {
                const reduxState = JSON.parse(sessionStorage.getItem('reduxState') || '{}');
                token = reduxState?.auth?.user?.token || reduxState?.auth?.user?.accessToken;
            } catch (e) {
                // Silently fail if reduxState is not yet available
            }
        }

        if (token) {
            // Only attach token to our primary backend API
            // External APIs like sarahtech.com or TenderAPI (172.10.10.75) use their own keys and might fail with Bearer tokens
            const isInternalApi = config.url && config.url.includes(API_BASE_URL);

            if (isInternalApi) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

axios.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            const data = error.response.data;
            const message = data?.status || data?.message || "Session expired. Please log in again.";

            // Store the message in localStorage so it survives the redirect and can be shown on the login page
            localStorage.setItem('logout_message', message);

            // Clear session and redirect to login
            sessionStorage.clear();

            // Small delay to ensure redirect happens
            setTimeout(() => {
                window.location.href = '/';
            }, 100);

            return new Promise(() => { }); // Stop further error propagation
        }

        if (error.response && error.response.status === 403) {
            console.error('Unauthorized access detected');
        }
        return Promise.reject(error);
    }
);

// --- Global Fetch Interceptor (Monkey-patching) ---

const originalFetch = window.fetch;
window.fetch = async (...args) => {
    let [resource, config] = args;

    // Check if it's an API call to our backend
    const resourceUrl = typeof resource === 'string' ? resource : (resource instanceof Request ? resource.url : '');
    const isApiCall = resourceUrl.includes(API_BASE_URL);

    if (isApiCall) {
        config = config || {};

        // Handle Request object vs plain URL
        if (resource instanceof Request) {
            // If it's a Request object, we might need to clone it to modify headers
            // but for simplicity in this app, most calls are plain URLs
        } else {
            const headers = config.headers || {};

            // Get token
            let token = sessionStorage.getItem('authToken');
            if (!token) {
                try {
                    const reduxState = JSON.parse(sessionStorage.getItem('reduxState') || '{}');
                    token = reduxState?.auth?.user?.token || reduxState?.auth?.user?.accessToken;
                } catch (e) { }
            }

            if (token) {
                const authHeader = `Bearer ${token}`;

                if (headers instanceof Headers) {
                    headers.set('Authorization', authHeader);
                } else if (Array.isArray(headers)) {
                    const hasAuth = headers.some(h => h[0].toLowerCase() === 'authorization');
                    if (!hasAuth) headers.push(['Authorization', authHeader]);
                } else {
                    headers['Authorization'] = authHeader;
                }
                config.headers = headers;
            }
        }
    }

    const response = await originalFetch(resource, config);

    // Handle global 401 for fetch as well
    if (response.status === 401) {
        let message = "Session expired. Please log in again.";
        try {
            const clonedResponse = response.clone();
            const data = await clonedResponse.json();
            message = data?.status || data?.message || message;
        } catch (e) {
            // Not JSON or other error
        }
        localStorage.setItem('logout_message', message);
        sessionStorage.clear();
        setTimeout(() => {
            window.location.href = '/';
        }, 100);
    }

    return response;
};

// --- Global Link Interceptor (The "Blanket" Fix) ---
// This handles any remaining <a> tags that haven't been manually updated.
if (typeof document !== 'undefined') {
    document.addEventListener('click', async (e) => {
        // Find the nearest <a> tag from the clicked element
        const link = e.target.closest('a');

        // If it's a link to our API's file view endpoint
        if (link && link.href && link.href.includes('/api/Files/view')) {
            e.preventDefault(); // Stop the default browser navigation
            try {
                // Fetch the file using our intercepted axios (which includes the token)
                const response = await axios.get(link.href, { responseType: 'blob' });
                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                const blobUrl = URL.createObjectURL(blob);

                // Open the secure blob URL in a new tab
                window.open(blobUrl, '_blank');
            } catch (err) {
                console.error("Global link interceptor failed:", err);
                // Fallback: If everything fails, try to open normally
                window.open(link.href, '_blank');
            }
        }
    }, true); // Use capture phase to ensure we catch it early

    // --- Global Image Interceptor ---
    // Automatically handles <img> tags pointing to the secure API
    const imageBlobCache = new Map(); // URL -> Promise<blobUrl>

    const processSecureImage = async (img) => {
        const src = img.getAttribute('src');
        if (!src || !src.includes(API_BASE_URL) || src.startsWith('blob:')) return;

        // Avoid redundant processing if already secured for this specific URL
        if (img.dataset.securedSrc === src) return;

        // If we are already securing this image, don't start again
        if (img.dataset.securing === "true") return;
        img.dataset.securing = "true";

        try {
            let blobUrl;
            if (imageBlobCache.has(src)) {
                blobUrl = await imageBlobCache.get(src);
            } else {
                const fetchPromise = (async () => {
                    const response = await fetch(src);
                    if (!response.ok) throw new Error('Fetch failed');
                    const blob = await response.blob();
                    return URL.createObjectURL(blob);
                })();
                imageBlobCache.set(src, fetchPromise);
                blobUrl = await fetchPromise;
            }

            if (blobUrl) {
                img.dataset.securedSrc = src;
                img.src = blobUrl;
            }
        } catch (err) {
            console.error("Failed to secure image:", src, err);
            // Don't cache failed requests indefinitely
            imageBlobCache.delete(src);
        } finally {
            delete img.dataset.securing;
        }
    };

    // Watch for new images and src changes
    const imageObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === 1) { // Element node
                        if (node.tagName === 'IMG') processSecureImage(node);
                        node.querySelectorAll?.('img').forEach(processSecureImage);
                    }
                });
            } else if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
                if (mutation.target.tagName === 'IMG') {
                    // Check if the new src is actually different from the one we secured
                    const newSrc = mutation.target.getAttribute('src');
                    if (newSrc !== mutation.target.dataset.securedSrc) {
                        processSecureImage(mutation.target);
                    }
                }
            }
        }
    });

    // Start observing when document is ready
    if (document.body) {
        imageObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
        document.querySelectorAll('img').forEach(processSecureImage);
    } else {
        window.addEventListener('DOMContentLoaded', () => {
            imageObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
            document.querySelectorAll('img').forEach(processSecureImage);
        });
    }
}

export default axios;
